import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollLock } from "../../lib/hooks";
import { profile } from "../../data/site";
import {
  ArrowUpRight,
  Award,
  Briefcase,
  Code,
  Copy,
  Download,
  Folder,
  GitHub,
  Home,
  Instagram,
  LinkedIn,
  Mail,
  Message,
  Moon,
  Layers,
  Search,
  Sun,
  Terminal,
} from "../../lib/icons";

const isMac = () =>
  typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform || "");

/**
 * ⌘K launcher. Everything the site can do, one keystroke away.
 * Mounted always but renders nothing until opened, so it costs nothing at rest.
 */
const CommandPalette = ({ theme, onToggleTheme, onToast }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useScrollLock(open);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setCursor(0);
  }, []);

  const actions = useMemo(
    () => [
      { id: "home", group: "Go", label: "Home", icon: Home, keywords: "start intro hero", run: () => navigate("/") },
      { id: "work", group: "Go", label: "Experience & Résumé", icon: Briefcase, keywords: "resume cv jobs career history", run: () => navigate("/resume") },
      { id: "projects", group: "Go", label: "Projects", icon: Folder, keywords: "portfolio work builds", run: () => navigate("/projects") },
      { id: "contact", group: "Go", label: "Contact", icon: Message, keywords: "email message reach out hello", run: () => navigate("/contact") },
      {
        id: "resume-pdf",
        group: "Actions",
        label: "Download résumé (PDF)",
        icon: Download,
        keywords: "cv pdf download file",
        run: () => window.open(profile.resume, "_blank", "noopener"),
      },
      {
        id: "copy-email",
        group: "Actions",
        label: `Copy email — ${profile.email}`,
        icon: Copy,
        keywords: "clipboard mail address",
        run: async () => {
          try {
            await navigator.clipboard.writeText(profile.email);
            onToast("Email copied to clipboard");
          } catch (_) {
            window.location.href = `mailto:${profile.email}`;
          }
        },
      },
      {
        id: "theme",
        group: "Actions",
        label: theme === "dark" ? "Switch to light mode" : "Switch to dark mode",
        icon: theme === "dark" ? Sun : Moon,
        keywords: "theme dark light appearance colour color",
        run: () => {
          onToggleTheme();
          onToast(theme === "dark" ? "Light mode on" : "Dark mode on");
        },
      },
      {
        id: "email",
        group: "Elsewhere",
        label: "Send an email",
        icon: Mail,
        keywords: "mailto contact",
        external: true,
        run: () => { window.location.href = `mailto:${profile.email}`; },
      },
      { id: "gh", group: "Elsewhere", label: "GitHub", icon: GitHub, keywords: "code repos source", external: true, run: () => window.open("https://github.com/ourialkada", "_blank", "noopener") },
      { id: "li", group: "Elsewhere", label: "LinkedIn", icon: LinkedIn, keywords: "professional network job", external: true, run: () => window.open("https://www.linkedin.com/in/ouri-alkada-46267a109/", "_blank", "noopener") },
      { id: "ig", group: "Elsewhere", label: "Instagram", icon: Instagram, keywords: "social photos", external: true, run: () => window.open("https://www.instagram.com/ourialkada/", "_blank", "noopener") },
      {
        id: "arch",
        group: "Go",
        label: "System design — a platform I built",
        icon: Layers,
        keywords: "architecture diagram azure cloud system infrastructure design",
        run: () => navigate("/#architecture"),
      },
      {
        id: "shell",
        group: "Actions",
        label: "Try the terminal on the home page",
        icon: Terminal,
        keywords: "shell console command line cli play interactive",
        run: () => navigate("/"),
      },
      { id: "awards", group: "Go", label: "Publication, awards & education", icon: Award, keywords: "school degree honours honors gpa paper published research", run: () => navigate("/resume#education") },
      { id: "stack", group: "Go", label: "Tech stack", icon: Code, keywords: "skills languages tools", run: () => navigate("/resume#skills") },
    ],
    [navigate, onToast, onToggleTheme, theme]
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter((a) =>
      `${a.label} ${a.group} ${a.keywords}`.toLowerCase().includes(q)
    );
  }, [actions, query]);

  // Global shortcut.
  useEffect(() => {
    const onKey = (event) => {
      const k = event.key.toLowerCase();
      if (k === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((o) => !o);
      }
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    if (open) {
      // Focus after paint so the browser doesn't scroll the page under us.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  // Keep the highlighted row in view without jumping the whole page.
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector('[data-active="true"]');
    node?.scrollIntoView({ block: "nearest" });
  }, [cursor, open]);

  const run = useCallback(
    (action) => {
      close();
      action.run();
    },
    [close]
  );

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((c) => (results.length ? (c + 1) % results.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((c) => (results.length ? (c - 1 + results.length) % results.length : 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const action = results[cursor];
      if (action) run(action);
    }
  };

  // The trigger lives in the nav; expose the opener on window for it.
  useEffect(() => {
    window.__openPalette = () => setOpen(true);
    return () => {
      delete window.__openPalette;
    };
  }, []);

  if (!open) return null;

  let lastGroup = null;

  return (
    <div
      className="palette__scrim"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
      >
        <div className="palette__field">
          <Search size={18} />
          <input
            ref={inputRef}
            className="palette__input"
            placeholder="Search pages, actions, links…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search commands"
            autoComplete="off"
            spellCheck="false"
          />
          <kbd>esc</kbd>
        </div>

        <div className="palette__list" ref={listRef}>
          {results.length === 0 && (
            <p className="palette__empty">No matches for “{query}”</p>
          )}
          {results.map((action, i) => {
            const Icon = action.icon;
            const showGroup = action.group !== lastGroup;
            lastGroup = action.group;
            return (
              <React.Fragment key={action.id}>
                {showGroup && <div className="palette__group">{action.group}</div>}
                <button
                  type="button"
                  className="palette__item"
                  data-active={i === cursor}
                  onMouseMove={() => setCursor(i)}
                  onClick={() => run(action)}
                >
                  <span className="palette__icon">
                    <Icon size={15} />
                  </span>
                  <span className="palette__label">{action.label}</span>
                  {action.external ? (
                    <ArrowUpRight size={14} className="palette__hint" />
                  ) : (
                    <span className="palette__hint">↵</span>
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        <div className="palette__foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> select</span>
          <span><kbd>{isMac() ? "⌘" : "ctrl"}</kbd><kbd>K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
